import { TeamDTO } from "./team";
import { UserDTO } from "./user";
import { Nullable } from "./utils";

export type PromptOwnerType = "user" | "team" | "public";

export const PROMPT_CATEGORY_IDS = [
  "targeting",
  "brief",
  "signals",
  "approach",
  "writing",
  "qualification",
  "other",
] as const;

export type PromptCategoryId = (typeof PROMPT_CATEGORY_IDS)[number];

export type PromptTranslation = {
  description: string;
  content: string;
  slug?: string;
};

export type PromptTranslations = {
  fr: PromptTranslation;
  en: PromptTranslation;
};

export type PromptBaseDTO = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  title?: string;
  description?: string;
  content: string;
  ownerType: PromptOwnerType;
  ownerId: Nullable<string>;
  owner: Nullable<UserDTO | TeamDTO>;
  author?: UserDTO;
  lastUpdatedBy?: UserDTO;
  categoryId?: PromptCategoryId;
  translations?: PromptTranslations;
  icon?: string;
  slug?: string;
  isArchived?: boolean;
  /**
   * Whether the assistant may bring this tactic out on its own.
   *
   * Carried by the tactic, not by the reader: a team's tactic is one row its
   * manager answers for, and a member muting it for themselves would leave the
   * rest of the team with a rule nobody decided.
   *
   * Off is the safe state — a tactic reaches the model only once its owner has
   * said so — which is why every tactic that existed before this field was
   * switched off by the migration that added it.
   */
  isAutoEnabled: boolean;
};

export type UserPromptDTO = PromptBaseDTO & {
  ownerType: "user";
  owner: UserDTO;
};

export type TeamPromptDTO = PromptBaseDTO & {
  ownerType: "team";
  owner: TeamDTO;
};

export type PublicPromptDTO = PromptBaseDTO & {
  ownerType: "public";
  owner: null;
};

export type PromptDTO = UserPromptDTO | TeamPromptDTO | PublicPromptDTO;

export type ToggleFavoritePromptDTO = {
  isFavorite: boolean;
};

export type PinnedContext = 'company' | 'chat';

export type TogglePinnedPromptDTO = {
  isPinned: boolean;
  isLimitReached: boolean;
};

export type ReorderPinnedPromptsDTO = {
  orderedPromptIds: string[];
  context: PinnedContext;
};

export type UserPromptsPreferencesDTO = {
  favoritePromptIds: string[];
  pinnedPromptIds: Record<PinnedContext, string[]>;
};

/** A team's ordered default pins, per context — what a new member is seeded with. */
export type TeamPinnedPromptsDefaultDTO = Record<PinnedContext, string[]>;

export type ApplyTeamPinnedPromptsDefaultResultDTO = {
  affectedMemberCount: number;
};

/**
 * One of a member's pins, as a manager sees it from the member's detail page.
 *
 * `isPersonal` pins belong to the member's own tactics: the manager sees that
 * a slot is taken, never which tactic it is, and cannot move or remove it.
 */
export type MemberPinnedPromptDTO = {
  readonly promptId: string;
  readonly isTeamDefault: boolean;
  readonly isPersonal: boolean;
};
